<template>
  <Hint padding="md" :type="type" class="cursor-pointer" @click="toggle">
    <View align="center" justify="space-between">
      <View align="center" justify="space-between" gap="sm" class="icon-and-label">
        <div class="icon" v-if="$slots.icon">
          <slot name="icon" />
        </div>
        <Icon v-else-if="icon" :name="icon" size="extra-small" class="icon" />

        <Text v-if="label" variant="label-small" color="primary" class="label-text">
          {{ label }}
        </Text>
      </View>

      <!-- Use the same icon and rotate it to avoid micro-jumps -->
      <Icon size="extra-small" name="chevron-down" :class="{ rotated: expanded }" />
    </View>

    <transition @before-enter="beforeEnter" @enter="enter" @before-leave="beforeLeave" @leave="leave">
      <View v-show="expanded" direction="column" gap="md" class="collapsible-content" ref="content">
        <View />
        <slot />
      </View>
    </transition>
  </Hint>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { IconName } from '../values/icons.const';
import Hint from './hint.vue';
import Icon from './icon.vue';
import Text from './text.vue';
import View from './view/view.vue';

const props = withDefaults(
  defineProps<{
    icon?: IconName;
    type?: 'info' | 'error';
    label?: string;
    initiallyExpanded?: boolean;
  }>(),
  {
    type: 'info',
    initiallyExpanded: false,
  },
);

const expanded = ref(props.initiallyExpanded);

function toggle() {
  expanded.value = !expanded.value;
}

function beforeEnter(el: Element) {
  const element = el as HTMLElement;
  element.style.maxHeight = '0';
  element.style.opacity = '0';
}

function enter(el: Element, done: () => void) {
  const element = el as HTMLElement;
  const height = element.scrollHeight + 'px';
  element.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';

  requestAnimationFrame(() => {
    const onTransitionEnd = () => done();

    element.addEventListener('transitionend', onTransitionEnd, { once: true });

    element.style.maxHeight = height;
    element.style.opacity = '1';
  });
}

function beforeLeave(el: Element) {
  const element = el as HTMLElement;
  element.style.maxHeight = element.scrollHeight + 'px';
  element.style.opacity = '1';
}

function leave(el: Element, done: () => void) {
  const element = el as HTMLElement;
  element.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';

  requestAnimationFrame(() => {
    const onTransitionEnd = () => done();

    element.addEventListener('transitionend', onTransitionEnd, { once: true });

    element.style.maxHeight = '0';
    element.style.opacity = '0';
  });
}
</script>

<style scoped lang="scss">
.rotated {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.collapsible-content {
  overflow: hidden;
}
</style>
